import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { HeaderIconButton } from "./HeaderIconButton";
import { HeaderTitleSection } from "./HeaderTitleSection";
import { HeaderTrailing } from "./HeaderTrailing";
import type { ScreenHeaderAction } from "./types";

export type { ScreenHeaderAction } from "./types";

type Props = {
	title?: string;
	subtitle?: string;
	titleContent?: React.ReactNode;
	leadingAction?: ScreenHeaderAction;
	actions?: ScreenHeaderAction[];
	/** Custom trailing content rendered in place of actions (e.g. Menu) */
	trailingContent?: React.ReactNode;
	transparent?: boolean;
	children?: React.ReactNode;
};

export const ScreenHeaderBar = ({
	title,
	subtitle,
	titleContent,
	leadingAction,
	actions = [],
	trailingContent,
	transparent,
	children,
}: Props) => {
	const theme = useAppTheme();
	const { isDesktopWeb } = useDevice();
	const insets = useSafeAreaInsets();
	const topPadding = isDesktopWeb ? 12 : Math.max(insets.top, 12);
	const iconColor = transparent
		? theme.colors.white
		: theme.colors.onSurfaceVariant;

	return (
		<Box
			role="banner"
			width="100%"
			backgroundColor={
				transparent ? "transparent" : theme.colors.elevation.level1
			}
		>
			<Box paddingTop={topPadding} paddingBottom={12} gap={12}>
				<Box
					flexDirection="row"
					alignItems="flex-start"
					justifyContent="space-between"
					paddingHorizontal={16}
					columnGap={16}
				>
					<Box
						flex={1}
						flexDirection="row"
						alignItems="flex-start"
						columnGap={8}
					>
						{leadingAction ? (
							<HeaderIconButton
								action={leadingAction}
								color={iconColor}
								marginLeft={-8}
							/>
						) : null}
						<HeaderTitleSection
							title={title}
							subtitle={subtitle}
							titleContent={titleContent}
						/>
					</Box>
					<HeaderTrailing
						actions={actions}
						trailingContent={trailingContent}
						iconColor={iconColor}
					/>
				</Box>
				{children}
			</Box>
		</Box>
	);
};
